import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  CircleDot,
  Download,
  Minus,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  claimsControlChart,
  claimsPerThousand,
  warrantyCostByLine,
  warrantyCostForecast,
} from "@/lib/warranty-data";
import {
  EXECUTIVE_PREVIEW_DISCLOSURE,
  REGULATORY_AUTHORITY_DISCLOSURE,
  REGULATORY_SYNTHETIC_DISCLOSURE,
  ExecutivePreviewNotice,
  RegulatorySyntheticPanel,
} from "@/components/ExecutiveGovernanceNotice";
import { HistoricalFailureModes } from "@/components/HistoricalFailureModes";
import { DemoInvestigationJourney } from "@/components/DemoInvestigationJourney";

export const Route = createFileRoute("/executive")({
  head: () => ({
    meta: [
      { title: "Executive Summary — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "One-screen executive briefing: warranty cost, claims-per-1000, forecast variance, control-chart health, and top risk components.",
      },
      { property: "og:title", content: "Executive Summary — Broadview Warranty Intelligence" },
      {
        property: "og:description",
        content:
          "Leadership-ready rollup of warranty cost, claims, forecast variance, and component risk.",
      },
    ],
  }),
  component: ExecutiveSummary,
});

// ---------- helpers ----------
const fmtMoney = (v: number) => `$${v.toFixed(2)}M`;
const fmtPct = (v: number, digits = 1) => `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
const deltaTone = (v: number, goodWhenNegative = true) =>
  v === 0
    ? "text-muted-foreground"
    : v < 0 === goodWhenNegative
      ? "text-success"
      : "text-destructive";

function DeltaIcon({ v, goodWhenNegative = true }: { v: number; goodWhenNegative?: boolean }) {
  if (v === 0) return <Minus className="h-3.5 w-3.5" />;
  const good = v < 0 === goodWhenNegative;
  const Up = good ? ArrowDownRight : ArrowUpRight;
  const Down = good ? ArrowUpRight : ArrowDownRight;
  return v < 0 ? <Up className="h-3.5 w-3.5" /> : <Down className="h-3.5 w-3.5" />;
}

// ---------- page ----------
// ---------- PDF export ----------
async function generateExecutivePdf() {
  const { default: jsPDF } = await import("jspdf");

  // Recompute metrics for the PDF (mirrors summary above)
  const actuals = warrantyCostForecast.filter((m) => m.actual !== null);
  const lastActual = actuals[actuals.length - 1];
  const prevActual = actuals[actuals.length - 2];
  const lastForecast = warrantyCostForecast.find((m) => m.month === lastActual.month)!.forecast;
  const ytdActual = actuals.reduce((s, m) => s + (m.actual ?? 0), 0);
  const costMoM = ((lastActual.actual! - prevActual.actual!) / prevActual.actual!) * 100;
  const forecastVar = ((lastActual.actual! - lastForecast) / lastForecast) * 100;
  const last6 = actuals.slice(-6);
  const mape =
    (last6.reduce((s, m) => {
      const f = warrantyCostForecast.find((x) => x.month === m.month)!.forecast;
      return s + Math.abs((m.actual! - f) / m.actual!);
    }, 0) /
      last6.length) *
    100;

  const c1000 = claimsPerThousand[claimsPerThousand.length - 1].evx;
  const c1000Prev = claimsPerThousand[claimsPerThousand.length - 2].evx;
  const c1000Delta = ((c1000 - c1000Prev) / c1000Prev) * 100;
  const c1000Target = claimsPerThousand[claimsPerThousand.length - 1].target;

  const components = claimsControlChart.map((c) => {
    const vals = c.series.map((p) => p.claims);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const mr = vals.slice(1).reduce((a, v, i) => a + Math.abs(v - vals[i]), 0) / (vals.length - 1);
    const sigma = mr / 1.128;
    const ucl = mean + 3 * sigma;
    const lcl = Math.max(0, mean - 3 * sigma);
    const breached = vals.some((v) => v > ucl || v < lcl);
    const latest = vals[vals.length - 1];
    const prior = vals[vals.length - 2];
    const mom = ((latest - prior) / prior) * 100;
    return {
      key: c.key,
      label: c.label,
      spec: c.spec,
      breached,
      latest,
      mom,
      total: vals.reduce((a, b) => a + b, 0),
    };
  });
  const breaches = components.filter((c) => c.breached).length;
  const topRisk = [...components].sort(
    (a, b) => b.latest * (b.mom + 100) - a.latest * (a.mom + 100),
  )[0];
  const watchlist = [...components]
    .sort((a, b) => b.latest * Math.abs(b.mom) - a.latest * Math.abs(a.mom))
    .slice(0, 5);

  const openClaims = components.reduce((s, c) => s + c.latest, 0) * 7;
  const avgCost = Math.round((lastActual.actual! * 1_000_000) / openClaims);

  const lineSummary = warrantyCostByLine.map((l) => {
    const lActuals = l.series.filter((s) => s.actual !== null);
    const last = lActuals[lActuals.length - 1];
    const lastF = l.series.find((s) => s.month === last.month)!.forecast;
    const variance = ((last.actual! - lastF) / lastF) * 100;
    return { label: l.label, actual: last.actual!, forecast: lastF, variance };
  });

  const fMoney = (v: number) => `$${v.toFixed(2)}M`;
  const fPct = (v: number, d = 1) => `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`;

  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 40;
  let y = 90;

  const ensureSpace = (needed: number) => {
    if (y + needed > H - 40) {
      pdf.addPage();
      y = M;
    }
  };
  const heading = (text: string, size = 12) => {
    ensureSpace(size + 10);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(size);
    pdf.setTextColor(20, 20, 24);
    pdf.text(text, M, y);
    y += size + 4;
  };
  const body = (text: string, size = 10, color: [number, number, number] = [60, 60, 66]) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(color[0], color[1], color[2]);
    const lines = pdf.splitTextToSize(text, W - M * 2);
    ensureSpace(lines.length * (size + 2));
    pdf.text(lines, M, y);
    y += lines.length * (size + 2);
  };
  const rule = () => {
    ensureSpace(12);
    pdf.setDrawColor(220, 220, 224);
    pdf.line(M, y, W - M, y);
    y += 12;
  };

  // Cover header
  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, W, 60, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(255, 255, 255);
  pdf.text("Broadview Warranty Intelligence", M, 28);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(180, 190, 210);
  pdf.text(`Executive Summary · ${lastActual.month}`, M, 46);
  pdf.setFontSize(9);
  pdf.text(`Generated ${new Date().toLocaleDateString()}`, W - M, 46, { align: "right" });

  heading("Data Classification", 11);
  body(EXECUTIVE_PREVIEW_DISCLOSURE, 9, [120, 120, 130]);
  y += 6;
  rule();

  // Narrative
  heading("Executive Narrative", 13);
  body(
    `Warranty cost ran ${forecastVar < 0 ? "below" : "above"} AI forecast by ${Math.abs(forecastVar).toFixed(1)}% in ${lastActual.month}. ${breaches > 0 ? `${breaches} component${breaches > 1 ? "s" : ""} breaching ±3σ control limits — ${topRisk.label} is the top driver.` : `All ${components.length} monitored components are in statistical control.`} 6-month forecast MAPE: ${mape.toFixed(1)}%.`,
    10,
  );
  y += 6;
  rule();

  // KPI grid (2 cols x 3 rows)
  heading("Key Performance Indicators", 12);
  const kpis: Array<[string, string, string, number]> = [
    [
      `Warranty Cost · ${lastActual.month}`,
      fMoney(lastActual.actual!),
      `YTD ${fMoney(ytdActual)}`,
      costMoM,
    ],
    ["Claims / 1,000 · EV-X @ MIS 12", c1000.toFixed(1), `Target ≤ ${c1000Target}`, c1000Delta],
    ["Forecast Variance", fPct(forecastVar), `MAPE ${mape.toFixed(1)}% · 6mo`, forecastVar],
    ["Open Claims", openClaims.toLocaleString(), `${lastActual.month} run-rate`, 0],
    ["Avg Cost / Claim", `$${avgCost.toLocaleString()}`, "Direct + indirect", 0],
    [
      "SPC Health",
      `${components.length - breaches}/${components.length}`,
      breaches > 0 ? `${breaches} breaching ±3σ` : "All in control",
      0,
    ],
  ];
  const colW = (W - M * 2 - 12) / 2;
  const cardH = 58;
  const gridStart = y;
  kpis.forEach((k, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = M + col * (colW + 12);
    const cy = gridStart + row * (cardH + 10);
    pdf.setDrawColor(220, 220, 224);
    pdf.setFillColor(250, 250, 252);
    pdf.roundedRect(x, cy, colW, cardH, 4, 4, "FD");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 120, 130);
    pdf.text(k[0].toUpperCase(), x + 10, cy + 14);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(20, 20, 24);
    pdf.text(k[1], x + 10, cy + 32);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 130);
    pdf.text(k[2], x + 10, cy + 48);
    if (k[3] !== 0) {
      const good = k[3] < 0;
      const dc: [number, number, number] = good ? [22, 163, 74] : [220, 38, 38];
      pdf.setTextColor(dc[0], dc[1], dc[2]);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text(fPct(k[3]), x + colW - 10, cy + 32, { align: "right" });
    }
  });
  y = gridStart + 3 * (cardH + 10) + 4;
  rule();

  // Product line summary
  heading("Product Line Summary", 12);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(120, 120, 130);
  pdf.text("LINE", M, y);
  pdf.text("ACTUAL", M + 220, y, { align: "right" });
  pdf.text("FORECAST", M + 320, y, { align: "right" });
  pdf.text("VARIANCE", W - M, y, { align: "right" });
  y += 6;
  pdf.setDrawColor(220, 220, 224);
  pdf.line(M, y, W - M, y);
  y += 12;
  lineSummary.forEach((l) => {
    ensureSpace(18);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(30, 30, 34);
    pdf.text(l.label, M, y);
    pdf.text(fMoney(l.actual), M + 220, y, { align: "right" });
    pdf.text(fMoney(l.forecast), M + 320, y, { align: "right" });
    const good = l.variance < 0;
    const dc: [number, number, number] = good ? [22, 163, 74] : [220, 38, 38];
    pdf.setTextColor(dc[0], dc[1], dc[2]);
    pdf.setFont("helvetica", "bold");
    pdf.text(fPct(l.variance), W - M, y, { align: "right" });
    y += 16;
  });
  y += 4;
  rule();

  // Component watchlist
  heading("Component Watchlist (Top 5)", 12);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(120, 120, 130);
  pdf.text("COMPONENT", M, y);
  pdf.text("LATEST", M + 240, y, { align: "right" });
  pdf.text("MoM", M + 310, y, { align: "right" });
  pdf.text("TOTAL", M + 380, y, { align: "right" });
  pdf.text("STATUS", W - M, y, { align: "right" });
  y += 6;
  pdf.setDrawColor(220, 220, 224);
  pdf.line(M, y, W - M, y);
  y += 12;
  watchlist.forEach((c) => {
    ensureSpace(18);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(30, 30, 34);
    pdf.text(c.label, M, y);
    pdf.text(String(c.latest), M + 240, y, { align: "right" });
    const good = c.mom < 0;
    const dc: [number, number, number] = good ? [22, 163, 74] : [220, 38, 38];
    pdf.setTextColor(dc[0], dc[1], dc[2]);
    pdf.setFont("helvetica", "bold");
    pdf.text(fPct(c.mom), M + 310, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(30, 30, 34);
    pdf.text(String(c.total), M + 380, y, { align: "right" });
    const sc: [number, number, number] = c.breached ? [220, 38, 38] : [22, 163, 74];
    pdf.setTextColor(sc[0], sc[1], sc[2]);
    pdf.setFont("helvetica", "bold");
    pdf.text(c.breached ? "Out of control" : "In control", W - M, y, { align: "right" });
    y += 16;
  });
  y += 4;
  rule();

  // Safety and regulatory domains remain decision-gated.
  heading("Safety & Regulatory Data", 12);
  body(REGULATORY_SYNTHETIC_DISCLOSURE, 10);
  body(REGULATORY_AUTHORITY_DISCLOSURE, 9, [120, 120, 130]);
  y += 8;

  // Footer on every page
  const pageCount = pdf.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p);
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 160);
    pdf.text(
      `Broadview Warranty Intelligence — Executive Summary · Page ${p} of ${pageCount}`,
      W / 2,
      H - 20,
      { align: "center" },
    );
  }

  const stamp = new Date().toISOString().slice(0, 10);
  downloadPdfBlob(pdf.output("blob"), `executive-summary-${stamp}.pdf`);
}

function downloadPdfBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 1000);
}

function ExecutiveSummary() {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await generateExecutivePdf();
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExporting(false);
    }
  };

  const summary = useMemo(() => {
    // Warranty cost: latest actual vs prior, plus YTD and forecast variance
    const actuals = warrantyCostForecast.filter((m) => m.actual !== null);
    const lastActual = actuals[actuals.length - 1];
    const prevActual = actuals[actuals.length - 2];
    const lastForecast = warrantyCostForecast.find((m) => m.month === lastActual.month)!.forecast;
    const ytdActual = actuals.reduce((s, m) => s + (m.actual ?? 0), 0);
    const ytdForecast = warrantyCostForecast
      .slice(0, actuals.length)
      .reduce((s, m) => s + m.forecast, 0);
    const costMoM = ((lastActual.actual! - prevActual.actual!) / prevActual.actual!) * 100;
    const forecastVar = ((lastActual.actual! - lastForecast) / lastForecast) * 100;

    // MAPE over last 6 months
    const last6 = actuals.slice(-6);
    const mape =
      (last6.reduce((s, m) => {
        const f = warrantyCostForecast.find((x) => x.month === m.month)!.forecast;
        return s + Math.abs((m.actual! - f) / m.actual!);
      }, 0) /
        last6.length) *
      100;

    // C/1000 — use EV-X at MIS 12 as headline
    const c1000 = claimsPerThousand[claimsPerThousand.length - 1].evx;
    const c1000Prev = claimsPerThousand[claimsPerThousand.length - 2].evx;
    const c1000Delta = ((c1000 - c1000Prev) / c1000Prev) * 100;
    const c1000Target = claimsPerThousand[claimsPerThousand.length - 1].target;

    // Control chart status — compute breaches per component
    const components = claimsControlChart.map((c) => {
      const vals = c.series.map((s) => s.claims);
      const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
      const mr =
        vals.slice(1).reduce((a, v, i) => a + Math.abs(v - vals[i]), 0) / (vals.length - 1);
      const sigma = mr / 1.128;
      const ucl = mean + 3 * sigma;
      const lcl = Math.max(0, mean - 3 * sigma);
      const breached = vals.some((v) => v > ucl || v < lcl);
      const latest = vals[vals.length - 1];
      const prior = vals[vals.length - 2];
      const mom = ((latest - prior) / prior) * 100;
      return {
        key: c.key,
        label: c.label,
        spec: c.spec,
        series: c.series.map((s) => ({ month: s.month, claims: s.claims })),
        mean,
        ucl,
        lcl,
        breached,
        latest,
        mom,
        total: vals.reduce((a, b) => a + b, 0),
      };
    });
    const breaches = components.filter((c) => c.breached).length;
    const topRisk = [...components].sort(
      (a, b) => b.latest * (b.mom + 100) - a.latest * (a.mom + 100),
    )[0];

    // Open claims (sum of latest month across components × scaling for narrative)
    const openClaims = components.reduce((s, c) => s + c.latest, 0) * 7;
    const openClaimsPrev =
      components.reduce((s, c) => s + c.series[c.series.length - 2].claims, 0) * 7;
    const openClaimsDelta = ((openClaims - openClaimsPrev) / openClaimsPrev) * 100;

    // Avg cost per claim (derived)
    const avgCost = (lastActual.actual! * 1_000_000) / openClaims;
    const avgCostPrev = (prevActual.actual! * 1_000_000) / openClaimsPrev;
    const avgCostDelta = ((avgCost - avgCostPrev) / avgCostPrev) * 100;

    // Watchlist — sort by latest × |mom|
    const watchlist = [...components]
      .sort((a, b) => b.latest * Math.abs(b.mom) - a.latest * Math.abs(a.mom))
      .slice(0, 5);

    // Product line summary
    const lineSummary = warrantyCostByLine.map((l) => {
      const lActuals = l.series.filter((s) => s.actual !== null);
      const last = lActuals[lActuals.length - 1];
      const lastF = l.series.find((s) => s.month === last.month)!.forecast;
      const variance = ((last.actual! - lastF) / lastF) * 100;
      return { key: l.key, label: l.label, actual: last.actual!, forecast: lastF, variance };
    });

    return {
      lastActual,
      ytdActual,
      ytdForecast,
      costMoM,
      forecastVar,
      mape,
      c1000,
      c1000Delta,
      c1000Target,
      components,
      breaches,
      topRisk,
      openClaims: Math.round(openClaims),
      openClaimsDelta,
      avgCost: Math.round(avgCost),
      avgCostDelta,
      watchlist,
      lineSummary,
    };
  }, []);

  const narrative = `Warranty cost ran ${summary.forecastVar < 0 ? "below" : "above"} AI forecast by ${Math.abs(summary.forecastVar).toFixed(1)}% in ${summary.lastActual.month}. ${summary.breaches > 0 ? `${summary.breaches} component${summary.breaches > 1 ? "s" : ""} breaching ±3σ control limits — ${summary.topRisk.label} is the top driver.` : `All ${summary.components.length} monitored components are in statistical control.`} 6-month forecast MAPE: ${summary.mape.toFixed(1)}%.`;

  const kpiTiles = [
    {
      label: `Warranty Cost · ${summary.lastActual.month}`,
      value: fmtMoney(summary.lastActual.actual!),
      sub: `YTD ${fmtMoney(summary.ytdActual)}`,
      delta: summary.costMoM,
      goodWhenNegative: true,
      sparkData: warrantyCostForecast
        .filter((m) => m.actual !== null)
        .map((m) => ({ x: m.month, y: m.actual })),
    },
    {
      label: "Claims / 1,000 · EV-X @ MIS 12",
      value: summary.c1000.toFixed(1),
      sub: `Target ≤ ${summary.c1000Target}`,
      delta: summary.c1000Delta,
      goodWhenNegative: true,
      sparkData: claimsPerThousand.map((m) => ({ x: m.mis, y: m.evx })),
    },
    {
      label: "Forecast Variance",
      value: fmtPct(summary.forecastVar, 1),
      sub: `MAPE ${summary.mape.toFixed(1)}% · 6mo`,
      delta: summary.forecastVar,
      goodWhenNegative: true,
      sparkData: warrantyCostForecast
        .filter((m) => m.actual !== null)
        .map((m) => ({ x: m.month, y: ((m.actual! - m.forecast) / m.forecast) * 100 })),
    },
    {
      label: "Open Claims",
      value: summary.openClaims.toLocaleString(),
      sub: `${summary.lastActual.month} run-rate`,
      delta: summary.openClaimsDelta,
      goodWhenNegative: true,
      sparkData: summary.components[0].series.map((s, i) => ({
        x: s.month,
        y: summary.components.reduce((a, c) => a + c.series[i].claims, 0) * 7,
      })),
    },
    {
      label: "Avg Cost / Claim",
      value: `$${summary.avgCost.toLocaleString()}`,
      sub: "Direct + indirect",
      delta: summary.avgCostDelta,
      goodWhenNegative: true,
      sparkData: warrantyCostForecast
        .filter((m) => m.actual !== null)
        .map((m, i) => {
          const claims = summary.components.reduce((a, c) => a + c.series[i].claims, 0) * 7;
          return { x: m.month, y: (m.actual! * 1_000_000) / claims };
        }),
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-4 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Broadview Warranty Intelligence</div>
              <div className="text-xs text-muted-foreground">Executive Summary</div>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <span className="hidden items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs text-success md:inline-flex">
              <CircleDot className="h-3 w-3 animate-pulse" /> Period: {summary.lastActual.month}
            </span>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Full dashboard
            </Link>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary shadow-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? "Exporting…" : "Export Executive"}
            </button>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-6 px-6 py-8">
        <ExecutivePreviewNotice />
        <DemoInvestigationJourney summaryOnly />

        {/* Hero / narrative */}
        <section className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Executive Summary</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Leadership briefing — warranty cost, claims, forecast accuracy, and component risk
                at a glance.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border bg-background/60 p-3 lg:max-w-2xl">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm leading-relaxed">{narrative}</p>
            </div>
          </div>
        </section>

        {/* KPI strip */}
        <section className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-5">
          {kpiTiles.map((k) => (
            <div key={k.label} className="rounded-xl border border-border bg-card p-4">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {k.label}
              </div>
              <div className="mt-2 flex flex-wrap items-baseline justify-between gap-1">
                <div className="text-2xl font-semibold tabular-nums">{k.value}</div>
                <div
                  className={`flex items-center gap-0.5 text-xs font-medium ${deltaTone(k.delta, k.goodWhenNegative)}`}
                >
                  <DeltaIcon v={k.delta} goodWhenNegative={k.goodWhenNegative} />
                  {fmtPct(k.delta)}
                </div>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">{k.sub}</div>
              <div className="mt-3 h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={k.sparkData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line
                      type="monotone"
                      dataKey="y"
                      stroke="var(--color-primary)"
                      strokeWidth={1.75}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </section>

        {/* Health signals */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          {/* Control chart status */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Target className="h-3.5 w-3.5" /> Control Chart Status
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <div className="text-3xl font-semibold tabular-nums">
                {summary.components.length - summary.breaches}
                <span className="text-muted-foreground">/{summary.components.length}</span>
              </div>
              <div className="text-sm text-muted-foreground">in control</div>
            </div>
            <div
              className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                summary.breaches > 0
                  ? "bg-destructive/15 text-destructive"
                  : "bg-success/15 text-success"
              }`}
            >
              {summary.breaches > 0 ? (
                <AlertTriangle className="h-3 w-3" />
              ) : (
                <CircleDot className="h-3 w-3" />
              )}
              {summary.breaches > 0 ? `${summary.breaches} breaching ±3σ` : "No SPC breaches"}
            </div>
            {summary.breaches > 0 && (
              <div className="mt-3 border-t border-border pt-3 text-xs">
                <div className="text-muted-foreground">Worst offender</div>
                <div className="mt-0.5 font-medium">{summary.topRisk.label}</div>
              </div>
            )}
          </div>

          {/* Forecast accuracy */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" /> AI Forecast Accuracy
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <div className="text-3xl font-semibold tabular-nums">
                {(100 - summary.mape).toFixed(1)}%
              </div>
              <div className="text-sm text-muted-foreground">6-mo accuracy</div>
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-xs font-medium text-success">
              <TrendingUp className="h-3 w-3" /> MAPE {summary.mape.toFixed(1)}%
            </div>
            <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
              Latest variance{" "}
              <span className={`font-medium ${deltaTone(summary.forecastVar)}`}>
                {fmtPct(summary.forecastVar)}
              </span>{" "}
              vs forecast
            </div>
          </div>

          {/* Top risk component */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5" /> Top Risk Component
            </div>
            <div className="mt-3 text-lg font-semibold leading-tight">{summary.topRisk.label}</div>
            <div className="text-xs text-muted-foreground">{summary.topRisk.spec}</div>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <div className="text-2xl font-semibold tabular-nums">{summary.topRisk.latest}</div>
                <div className="text-[11px] text-muted-foreground">
                  claims · {summary.lastActual.month}
                </div>
              </div>
              <div className="h-10 w-28">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={summary.topRisk.series}
                    margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                  >
                    <Line
                      type="monotone"
                      dataKey="claims"
                      stroke={
                        summary.topRisk.breached
                          ? "var(--color-destructive)"
                          : "var(--color-primary)"
                      }
                      strokeWidth={1.75}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        {/* Mid section: lines + mix */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-5">
          <div className="rounded-xl border border-border bg-card p-5 lg:col-span-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Cost by Product Line</div>
                <div className="text-xs text-muted-foreground">
                  Latest month actual vs AI forecast ($M)
                </div>
              </div>
            </div>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={summary.lineSummary}
                  layout="vertical"
                  margin={{ top: 4, right: 12, left: 12, bottom: 4 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={130}
                    tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "color-mix(in oklab, var(--color-muted) 40%, transparent)" }}
                    contentStyle={{
                      background: "var(--color-popover)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    formatter={(v: number, name: string) => [
                      fmtMoney(v),
                      name === "actual" ? "Actual" : "Forecast",
                    ]}
                  />
                  <Bar
                    dataKey="forecast"
                    fill="var(--color-accent)"
                    radius={[4, 4, 4, 4]}
                    barSize={10}
                  />
                  <Bar
                    dataKey="actual"
                    fill="var(--color-primary)"
                    radius={[4, 4, 4, 4]}
                    barSize={10}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              {summary.lineSummary.map((l) => (
                <div key={l.key} className="rounded-md border border-border bg-background/60 p-2">
                  <div className="truncate text-muted-foreground">{l.label}</div>
                  <div className={`mt-0.5 font-medium ${deltaTone(l.variance)}`}>
                    {fmtPct(l.variance)} vs fcst
                  </div>
                </div>
              ))}
            </div>
          </div>

          <HistoricalFailureModes className="lg:col-span-2" />
        </section>

        {/* Watchlist */}
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Component Watchlist</div>
              <div className="text-xs text-muted-foreground">
                Ranked by latest claim volume × month-over-month movement
              </div>
            </div>
            <Link to="/" className="text-xs text-primary hover:underline">
              View SPC charts →
            </Link>
          </div>
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Component</th>
                  <th className="px-3 py-2 text-right font-medium">Claims</th>
                  <th className="px-3 py-2 text-right font-medium">MoM</th>
                  <th className="px-3 py-2 text-right font-medium">12-mo Total</th>
                  <th className="px-3 py-2 text-left font-medium">σ Status</th>
                  <th className="px-3 py-2 text-right font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {summary.watchlist.map((c) => (
                  <tr key={c.key} className="border-t border-border">
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{c.label}</div>
                      <div className="text-[11px] text-muted-foreground">{c.spec}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium tabular-nums">{c.latest}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${deltaTone(c.mom)}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {c.mom < 0 ? (
                          <TrendingDown className="h-3 w-3" />
                        ) : (
                          <TrendingUp className="h-3 w-3" />
                        )}
                        {fmtPct(c.mom)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                      {c.total}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          c.breached
                            ? "bg-destructive/15 text-destructive"
                            : "bg-success/15 text-success"
                        }`}
                      >
                        {c.breached ? (
                          <>
                            <AlertTriangle className="h-3 w-3" /> Out of control
                          </>
                        ) : (
                          <>
                            <CircleDot className="h-3 w-3" /> In control
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="ml-auto h-8 w-24">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={c.series}
                            margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                          >
                            <Line
                              type="monotone"
                              dataKey="claims"
                              stroke={
                                c.breached ? "var(--color-destructive)" : "var(--color-primary)"
                              }
                              strokeWidth={1.5}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <RegulatorySyntheticPanel />

        <footer className="flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
          <div>Broadview Warranty Intelligence · Executive briefing</div>
          <Link to="/" className="text-primary hover:underline">
            Go to full analyst dashboard →
          </Link>
        </footer>
      </main>
    </div>
  );
}
